/**
 * In-form Terms accordion for classic Register.
 * Height + opacity driven on the UI thread (shared progress) — no layout transition thrash.
 * Body slot is a fixed size for loader and CMS text so paint never shifts.
 * RTL: row chrome via `flipRowStyle`; CMS HTML gets `dir` from `CmsPagesApiHtmlScroll`.
 */
import React, { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { View, Text, StyleSheet, useWindowDimensions } from 'react-native';
import Animated, {
  Extrapolation,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  type SharedValue,
} from 'react-native-reanimated';
import { REGISTER_TERMS_MOTION } from './registerTermsMotion';
import {
  getRegisterTermsCmsSnapshot,
  prefetchRegisterTermsHtml,
  subscribeRegisterTermsCms,
} from './registerTermsCms';
import { Clickable2 } from '../components/common/Clickable2';
import {
  GradientTickCheckbox,
  GRADIENT_TICK_CHECKBOX_FORM_SIZE,
} from '../components/common/GradientTickCheckbox';
import { MainLoader } from '../components/load-main/MainLoader';
import { CmsPagesApiHtmlScroll } from '../components/common/CmsPagesApiHtmlScroll';
import { cmsPageHtmlToDisplayText } from '../shared/utils/htmlToPlainText';
import { appTheme, useThemeColors, type ThemeColors } from '../shared/theme';
import { useTranslation } from '../shared/i18n';
import { useRtl } from '../shared/i18n/useRtl';

function createStyles(c: ThemeColors) {
  return StyleSheet.create({
    root: {
      width: '100%',
    },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: appTheme.spacing.xs,
      marginBottom: appTheme.spacing.sm,
      marginTop: appTheme.spacing.sm,
    },
    textBlock: {
      flexShrink: 1,
      flexGrow: 0,
      flexDirection: 'row',
      flexWrap: 'nowrap',
      alignItems: 'center',
      minWidth: 0,
    },
    agreeLine: {
      fontSize: 12,
      lineHeight: 16,
      fontWeight: '400',
      color: c.textSecondary,
      flexShrink: 1,
    },
    tcLink: {
      fontSize: 12,
      lineHeight: 16,
      color: c.primary,
      fontWeight: '600',
      textDecorationLine: 'underline',
    },
    panelClip: {
      width: '100%',
      overflow: 'hidden',
    },
    panelMeasure: {
      position: 'absolute',
      left: 0,
      right: 0,
      top: 0,
    },
    panel: {
      width: '100%',
      borderRadius: appTheme.borderRadius.md,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: c.borderLight ?? c.border,
      backgroundColor: c.surface,
      overflow: 'hidden',
    },
    panelHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: appTheme.spacing.md,
      paddingVertical: appTheme.spacing.sm,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: c.borderLight ?? c.border,
      gap: appTheme.spacing.sm,
    },
    panelTitle: {
      ...appTheme.typography.bodyMedium,
      color: c.text,
      flex: 1,
    },
    closeLink: {
      ...appTheme.typography.bodySmall,
      color: c.primary,
      fontWeight: '600',
    },
    /** Fixed slot — loader and CMS body must share exact width/height. */
    bodySlot: {
      width: '100%',
      overflow: 'hidden',
    },
    bodySlotInner: {
      ...StyleSheet.absoluteFillObject,
      width: '100%',
      height: '100%',
    },
    bodyCentered: {
      ...StyleSheet.absoluteFillObject,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: appTheme.spacing.md,
    },
    bodyScroll: {
      width: '100%',
      height: '100%',
    },
    bodyScrollContent: {
      paddingHorizontal: appTheme.spacing.md,
      paddingVertical: appTheme.spacing.sm,
    },
    cmsBodyText: {
      fontSize: 14,
      lineHeight: 22,
      color: c.textSecondary,
    },
    fallbackText: {
      ...appTheme.typography.bodySmall,
      lineHeight: 22,
      color: c.textSecondary,
      textAlign: 'center',
    },
    footer: {
      paddingHorizontal: appTheme.spacing.md,
      paddingTop: appTheme.spacing.sm,
      paddingBottom: appTheme.spacing.md,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: c.borderLight ?? c.border,
    },
    acceptBtn: {
      minHeight: 46,
      borderRadius: appTheme.borderRadius.md,
      backgroundColor: c.primary,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: appTheme.spacing.md,
    },
    acceptBtnLabel: {
      ...appTheme.typography.bodyMedium,
      fontWeight: '700',
      color: c.primaryText ?? c.onPrimary ?? c.white,
      textAlign: 'center',
    },
  });
}

export type RegisterTermsSectionProps = {
  checked: boolean;
  error?: boolean;
  /** 0 = collapsed, 1 = fully open — drives UI-thread accordion. */
  progress: SharedValue<number>;
  /** Discrete open flag for chrome (set when expand starts). */
  open: boolean;
  /** True while Phase A settle or accordion is busy — block double taps. */
  interaction_locked?: boolean;
  onRequestOpen: () => void;
  onRequestClose: () => void;
  onCheckedChange: (next: boolean) => void;
  onAccept: () => void;
};

export function RegisterTermsSection({
  checked,
  error,
  progress,
  open,
  interaction_locked = false,
  onRequestOpen,
  onRequestClose,
  onCheckedChange,
  onAccept,
}: RegisterTermsSectionProps) {
  const { t, locale } = useTranslation();
  const rtl = useRtl();
  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { height: window_h } = useWindowDimensions();
  const app_language = locale === 'ar' ? 'ar' : 'en';

  const cms = useSyncExternalStore(subscribeRegisterTermsCms, getRegisterTermsCmsSnapshot, getRegisterTermsCmsSnapshot);

  const [content_h, setContentH] = useState(0);
  const content_h_sv = useSharedValue(0);
  /** Keep panel subtree mounted after first open so close is a true reverse. */
  const [keep_panel, setKeepPanel] = useState(false);

  /** Fixed body viewport — identical for loader, CMS text, and fallback. */
  const body_slot_h = useMemo(
    () => Math.max(160, Math.min(Math.round(window_h * 0.46), 360)),
    [window_h],
  );

  useEffect(() => {
    void prefetchRegisterTermsHtml(app_language);
  }, [app_language]);

  useEffect(() => {
    if (open) {
      setKeepPanel(true);
      void prefetchRegisterTermsHtml(app_language);
    }
  }, [open, app_language]);

  useEffect(() => {
    content_h_sv.value = withTiming(content_h, {
      duration: content_h > 0 && content_h_sv.value === 0 ? 0 : content_h > 0 ? 160 : 0,
      easing: REGISTER_TERMS_MOTION.easing,
    });
  }, [content_h, content_h_sv]);

  const handle_accept = useCallback(() => {
    if (interaction_locked) {
      return;
    }
    onAccept();
    onRequestClose();
  }, [interaction_locked, onAccept, onRequestClose]);

  /**
   * Checkbox never opens the panel (link does that).
   * Panel open + check → accept + close.
   * Panel open + uncheck → uncheck only, panel stays open.
   * Panel closed → toggle check only.
   */
  const on_checkbox = useCallback(
    (next: boolean) => {
      if (interaction_locked) {
        return;
      }
      if (open) {
        if (next) {
          onAccept();
          onRequestClose();
          return;
        }
        onCheckedChange(false);
        return;
      }
      onCheckedChange(next);
    },
    [interaction_locked, onAccept, onCheckedChange, onRequestClose, open],
  );

  const on_link = useCallback(() => {
    if (interaction_locked) {
      return;
    }
    if (open) {
      onRequestClose();
    } else {
      onRequestOpen();
    }
  }, [interaction_locked, open, onRequestClose, onRequestOpen]);

  const panel_clip_style = useAnimatedStyle(() => ({
    height: progress.value * content_h_sv.value,
    opacity: interpolate(progress.value, [0, 0.18, 1], [0, 1, 1], Extrapolation.CLAMP),
  }));

  const html = cms.language === app_language ? cms.html : '';
  const is_loading = cms.language === app_language ? cms.is_loading || cms.status === 'idle' : true;
  const cms_text = cmsPageHtmlToDisplayText(html);
  const show_panel = keep_panel || open;

  return (
    <View style={styles.root}>
      <View style={[styles.headerRow, rtl.flipRowStyle]}>
        <GradientTickCheckbox
          testID="create-account-terms-checkbox"
          value={checked}
          onValueChange={on_checkbox}
          size={GRADIENT_TICK_CHECKBOX_FORM_SIZE}
          shape="square"
          borderRadius={6}
          tone="brand"
          error={!!error}
          haptics
          accessibilityLabel={t('auth.agreeToTerms')}
        />
        <Clickable2
          testID="create-account-terms-link"
          onPress={on_link}
          accessibilityRole="link"
          accessibilityLabel={`${t('auth.agreeToTerms')}${t('auth.termsAndConditions')}`}
          hitSlop={appTheme.hitSlop.small}
          style={styles.textBlock}>
          <Text
            style={[styles.agreeLine, rtl.textFollowLocale]}
            numberOfLines={1}
            adjustsFontSizeToFit
            minimumFontScale={0.85}>
            {t('auth.agreeToTerms')}
            <Text style={styles.tcLink}>{t('auth.termsAndConditions')}</Text>
          </Text>
        </Clickable2>
      </View>

      {show_panel ? (
        <Animated.View style={[styles.panelClip, panel_clip_style]} pointerEvents={open ? 'auto' : 'none'}>
          <View
            style={styles.panelMeasure}
            onLayout={e => {
              const next_h = Math.ceil(e.nativeEvent.layout.height);
              if (next_h > 0 && next_h !== content_h) {
                setContentH(next_h);
              }
            }}
            collapsable={false}>
            <View style={styles.panel}>
              <View style={[styles.panelHeader, rtl.flipRowStyle]}>
                <Text style={[styles.panelTitle, rtl.textFollowLocale]} numberOfLines={1}>
                  {t('settings.termsAndConditions')}
                </Text>
                <Clickable2
                  onPress={() => {
                    if (!interaction_locked) {
                      onRequestClose();
                    }
                  }}
                  accessibilityRole="button"
                  accessibilityLabel={t('common.close')}
                  hitSlop={appTheme.hitSlop.small}>
                  <Text style={styles.closeLink}>{t('common.close')}</Text>
                </Clickable2>
              </View>

              <View style={[styles.bodySlot, { height: body_slot_h }]}>
                {is_loading ? (
                  <View style={styles.bodyCentered} accessibilityState={{ busy: true }}>
                    <MainLoader size={36} color={colors.primary} />
                  </View>
                ) : cms_text ? (
                  <View style={styles.bodySlotInner}>
                    <CmsPagesApiHtmlScroll
                      html={html}
                      scrollViewStyle={styles.bodyScroll}
                      contentContainerStyle={styles.bodyScrollContent}
                      textStyle={styles.cmsBodyText}
                    />
                  </View>
                ) : (
                  <View style={styles.bodyCentered}>
                    <Text style={[styles.fallbackText, rtl.textFollowLocale]}>{t('auth.agreeToTerms')}</Text>
                  </View>
                )}
              </View>

              <View style={styles.footer}>
                <Clickable2
                  onPress={handle_accept}
                  haptics
                  style={styles.acceptBtn}
                  accessibilityRole="button"
                  accessibilityLabel={t('terms.acceptButton')}>
                  <Text style={styles.acceptBtnLabel}>{t('terms.acceptButton')}</Text>
                </Clickable2>
              </View>
            </View>
          </View>
        </Animated.View>
      ) : null}
    </View>
  );
}

export default RegisterTermsSection;
